/**
 * Project detail screen.
 *
 * Supervisor actions (Close project, Download recap PDF) appear inline
 * when the user has the supervisor or admin role. The recap link is also
 * available once the project status is `complete`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  DollarSign,
  ExternalLink,
  FileDown,
  FileText,
  Flag,
  HardHat,
  Image as ImageIcon,
  MoreHorizontal,
  Loader2,
  Mail,
  MapPin,
  MessageSquare,
  Mic,
  Navigation,
  Paperclip,
  Phone,
  Pencil,
  RotateCcw,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  openRecapPdf,
  useBidApproval,
  useCancelProject,
  useDeleteProject,
  useProject,
  useRestoreProject,
  useSendBid,
  useUpdateBid,
} from '@/api/projects';
import { ApproveScheduleButton } from '@/components/projects/ApproveScheduleButton';
import { ConfirmScheduleButton } from '@/components/projects/ConfirmScheduleButton';
import { TaskScheduleButton } from '@/components/projects/TaskScheduleButton';
import { AssignCrewButton } from '@/components/projects/AssignCrewButton';
import { AddTaskButton, EditTaskButton } from '@/components/projects/TaskEditor';
import { ProjectCrewCard } from '@/components/projects/ProjectCrewCard';
import { ProjectDetailsCard } from '@/components/projects/ProjectDetailsCard';
import { ConfirmTaskButton } from '@/components/projects/ConfirmTaskButton';
import { TaskStatusControl } from '@/components/projects/TaskStatusControl';
import { TaskTimelineEditor } from '@/components/projects/TaskTimelineEditor';
import { EditPeopleDialog } from '@/components/projects/EditPeopleDialog';
import { EditProjectDialog } from '@/components/projects/EditProjectDialog';
import { useTasks } from '@/api/tasks';
import { uploadProjectMedia, useAddProjectNote, useProjectActivity } from '@/api/activity';
import { attachmentFileUrl, uploadProjectDocument, useProjectDocuments } from '@/api/attachments';
import { docKindLabel, docKindTint } from '@/lib/docKind';
import { useProjectLineItems, useToggleLineItem } from '@/api/lineItems';
import { useDictation } from '@/lib/speech';
import { isoToDay, scheduleHoursLabel } from '@/lib/calendarGrid';
import { cn } from '@/lib/cn';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/Dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { SiteMap } from '@/components/ui/SiteMap';
import { MediaGallery } from '@/components/MediaGallery';
import { TaskChatPanel } from '@/components/TaskChatPanel';
import { CopyButton } from '@/components/ui/CopyButton';
import { destinationUrl } from '@/lib/maps';
import { ProjectStatusControl } from '@/components/projects/ProjectStatusControl';
import { useProjectAssignments } from '@/api/project-assignments';
import { isDirectlyDriven } from '@/lib/directlyDriven';
import { Avatar } from '@/components/ui/Avatar';
import { formatDateTime, formatMoneyCents, formatDate, formatDateOnly } from '@/lib/format';
import { summarizeLabor, phoneToTelHref, type LaborRates, type BidMode } from '@vari-crew/schemas';
import { useLaborRates } from '@/api/labor-rates';
import { useCostSettings } from '@/api/cost-settings';
import { useRecipients } from '@/api/recipients';
import { Input, Textarea } from '@/components/ui/Input';
import { RecipientAutocomplete } from '@/components/ui/RecipientAutocomplete';
import { BidBuilder, deriveLaborSeed, type BidInputs, type LaborSeed } from '@/components/ui/BidBuilder';
import type { RoutingContext } from '@/components/ui/MileageCalculatorModal';
import { RecipientPreview } from '@/components/bid/RecipientPreview';

/** 'HH:MM' (business-local) → '8:00 AM'. */
function hm(t: string): string {
  const [h = 0, m = 0] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

/** 'in_progress' → 'In progress' (task statuses have no shared label map yet). */
function taskStatusLabel(s: string): string {
  const text = s.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Row label for a task. A new task defaults to status 'scheduled' even before a
 * supervisor gives it a date/time, which reads as a misleading "Scheduled" — so
 * a default-status task with no scheduled_start shows "Unscheduled" instead.
 * Real lifecycle statuses (en route, complete, blocked…) keep their own label.
 */
function taskStateLabel(status: string, scheduledStart: string | null): string {
  if (status === 'scheduled' && !scheduledStart) return 'Unscheduled';
  return taskStatusLabel(status);
}

/**
 * A relative suffix for a hard deadline — "today", "in 3 days", "2 days ago" —
 * computed from local midnight so it doesn't drift with the time of day. Returns
 * '' when there's no date.
 */
function relativeDeadline(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfTarget = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((startOfTarget.getTime() - startOfToday.getTime()) / 86_400_000);
  if (days === 0) return 'today';
  if (days > 0) return `in ${days} day${days === 1 ? '' : 's'}`;
  const ago = -days;
  return `${ago} day${ago === 1 ? '' : 's'} ago`;
}

/** Priority → a sentence-case chip label, e.g. "High priority". */
function priorityLabel(priority: string): string {
  return `${priority.charAt(0).toUpperCase()}${priority.slice(1)} priority`;
}

/** Source → the lucide icon that fits its intake path. */
function sourceIcon(source: string) {
  if (source === 'email_forward') return Mail;
  if (source === 'ai_chat') return MessageSquare;
  return FileText;
}

/** Source enum → a human label (the raw value carries underscores). */
const SOURCE_LABEL: Record<string, string> = {
  form: 'Form',
  ai_chat: 'AI chat',
  pdf_upload: 'PDF upload',
  email_forward: 'Email',
  supervisor_manual: 'Manual entry',
};
function sourceLabel(source: string): string {
  return SOURCE_LABEL[source] ?? source.replace(/_/g, ' ');
}

export function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: project, isLoading, error } = useProject(id);
  const { data: tasks } = useTasks({ project_id: id, limit: 50, offset: 0 });
  // Direct project-anchored crew blocks (D-3) — cheap, and the Crew card's own
  // query dedupes with it. Feeds the directly-driven gate below.
  const { data: projectAssignments } = useProjectAssignments(id);
  const { data: me } = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const canManage = me?.role === 'supervisor' || me?.role === 'admin';
  const [editPeople, setEditPeople] = useState(false);
  const [editProject, setEditProject] = useState(false);
  // Which task's crew-only chat is expanded inline in the Tasks list (demo #7).
  // Supervisor/admin "adjust timeline" expansion for a completed task's row (Surface B).
  const [openTimelineTaskId, setOpenTimelineTaskId] = useState<string | null>(null);
  // Configured blended rate for the estimate (supervisor/admin only). Called
  // before the early returns to keep hook order stable.
  const { data: rates } = useLaborRates(canManage);
  // Cancel / soft-delete / restore (supervisor/admin). Mutation hooks are safe
  // to instantiate with an empty id before the early returns — they only fire
  // on .mutate(). Confirm-dialog state lives here for the same hook-order reason.
  const cancelProject = useCancelProject(id ?? '');
  const deleteProject = useDeleteProject(id ?? '');
  const restoreProject = useRestoreProject(id ?? '');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Back to wherever the user came from (calendar, Projects list, Home). On a
  // cold/direct load there's no history to pop, so fall back to a screen the
  // role actually has — crew lack the Projects list, so send them to Calendar.
  function goBack() {
    if (location.key !== 'default') navigate(-1);
    else navigate(me?.role === 'crew' ? '/calendar' : '/projects');
  }

  if (isLoading) return <div className="p-6 text-sm text-ink-dim">Loading…</div>;
  if (error) return <div className="p-6 text-sm text-status-blocked-fg">{(error as Error).message}</div>;
  if (!project) return null;

  // Calendar context for task (re)scheduling: the project's committed window
  // (approved → proposed → requested), its working-hours label, and the hard
  // deadline — passed to each TaskScheduleButton so the picker isn't blind.
  const committedSchedule =
    project.approved_schedule ?? project.proposed_schedule ?? project.requested_schedule ?? [];
  const committedDays = committedSchedule.map((s) => s.date);
  const committedHours = scheduleHoursLabel(
    committedSchedule,
    !project.approved_schedule &&
      !project.proposed_schedule &&
      !!project.requested_schedule_flexible,
  );
  const deadlineDay = isoToDay(project.deadline_date);

  const isComplete = project.status === 'complete';
  const isRequested = project.status === 'requested';
  const isCancelled = project.status === 'cancelled';
  const isDeleted = project.deleted_at != null;
  // "Before scheduled" (status 'requested') is the request/quote stage. Once the
  // project is committed (scheduled → … → complete, or blocked) work is real, so
  // containers that only make sense then — field activity, actuals, per-task
  // scheduling/assignment, the recap — switch on. The editable Bid card is the
  // mirror image: it shows ONLY while 'requested', since that's when quoting and
  // approval happen.
  const hasStarted = !isRequested;
  // Directly-driven (D-3): status is steered by hand, so the interactive
  // status control unlocks (Leo 2026-08-11: "I'm not seeing where I can
  // start"). Task-driven projects keep the rollup steering + static badge.
  // Unknown-while-loading counts as task-driven (safe default).
  const directlyDriven = isDirectlyDriven(projectAssignments, tasks) === true;
  // The requesting PM (the person who approves the bid). A bid that's been sent
  // but not yet approved can be opened from in-app, not just the email (§A6).
  const isRequestor = !!me?.id && me.id === project.pm_user_id;
  const bidAwaitingApproval = !!project.bid_sent_at && !project.bid_approved_at;
  // A sent bid (with a total) that hasn't been approved blocks scheduling — the
  // dates can't be confirmed and the request can't be approved until the budget
  // is approved (§A6). The approver, or a supervisor/admin on their behalf,
  // approves via the Budget approval card → which schedules.
  const bidApprovalPending = project.bid_mode === 'approval' && bidAwaitingApproval;
  // A supervisor-proposed schedule awaiting confirmation (sub-state of
  // 'requested'). The owning PM — or a supervisor/admin — can confirm it, but
  // NOT while a budget approval is still pending.
  const isProposed = isRequested && !!project.proposed_schedule?.length;
  const canConfirm =
    isProposed &&
    !bidApprovalPending &&
    (canManage || (me?.role === 'pm' && me?.id === project.pm_user_id));

  // PM-requested (pending) tasks are work-in-waiting: visible to the people who
  // act on them — supervisors/admins (who confirm) and PMs (who requested) — but
  // hidden from other viewers until confirmed.
  const visibleTasks = (tasks?.data ?? []).filter(
    (t) => !t.pending_confirmation || canManage || me?.role === 'pm',
  );

  // Labor estimate vs budget, recomputed from the project's tasks (EX.8) — the
  // decision signal a supervisor needs when reviewing a request.
  const laborEstimate =
    tasks?.data && tasks.data.length > 0
      ? summarizeLabor(
          tasks.data.map((t) => ({
            type: t.type as Parameters<typeof summarizeLabor>[0][number]['type'],
            crew_size_needed: t.crew_size_needed,
            estimated_duration_minutes: t.estimated_duration_minutes,
          })),
          project.delivery_install_budget_cents,
          rates?.estimate_default,
        )
      : null;

  // Safety net (§D): a committed project whose tasks still have no scheduled
  // time is invisible on the dispatch board — surface it so it can't be silently
  // missed. Supervisor-created projects auto-seed task times now; this catches
  // tasks added later, or a create with no working hours.
  const unscheduledTaskCount =
    tasks?.data.filter(
      (t) =>
        !t.scheduled_start &&
        !t.pending_confirmation &&
        t.status !== 'cancelled' &&
        t.status !== 'complete',
    ).length ?? 0;
  const showNeedsScheduling =
    canManage && !isRequested && !isComplete && unscheduledTaskCount > 0;

  async function downloadRecapPdf() {
    try {
      await openRecapPdf(project!.id);
    } catch (e) {
      toast.error(`Could not open recap PDF: ${(e as Error).message}`);
    }
  }


  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      <Button
        variant="ghost"
        size="sm"
        onClick={goBack}
        className="-ml-2 text-ink-dim"
        aria-label="Go back"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </Button>
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-semibold">{project.title}</h1>
            {/* Interactive on directly-driven projects (working range only);
                otherwise the plain badge — tasks steer those. */}
            <ProjectStatusControl
              projectId={project.id}
              status={project.status}
              enabled={canManage && directlyDriven && !isDeleted}
            />
          </div>
          <p className="text-sm text-ink-dim mt-1">
            <span className="text-ink-faint">Client</span>{' '}
            <span className="text-ink">{project.client_name}</span>
            {project.sap_order_number && (
              <span className="font-mono text-xs ml-2">· Order {project.sap_order_number}</span>
            )}
            {project.sap_project_id && (
              <span className="font-mono text-xs ml-2">· Project {project.sap_project_id}</span>
            )}
          </p>
          <div className="flex items-center gap-2 flex-wrap mt-2">
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                project.priority === 'high' || project.priority === 'urgent'
                  ? 'bg-amber-100 text-amber-900 border border-amber-200 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-900'
                  : 'bg-fill-soft text-ink-dim border border-line',
              )}
            >
              <Flag className="w-3 h-3" />
              {priorityLabel(project.priority)}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-fill-soft text-ink-dim border border-line px-2 py-0.5 text-xs font-medium">
              {(() => {
                const Icon = sourceIcon(project.source);
                return <Icon className="w-3 h-3" />;
              })()}
              Source · {sourceLabel(project.source)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* The crew's working screen for this project — Leo 2026-08-11:
              reachable from the project, not just from an assignee's Today.
              Always offered on committed projects (an appearing/disappearing
              button confused Ben immediately); on task-driven projects the
              crew screen swaps Start/Finish for the task list. */}
          {canManage && hasStarted && !isComplete && !isCancelled && !isDeleted && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/today/projects/${project.id}`)}
            >
              <HardHat className="w-3.5 h-3.5" />
              Crew view
            </Button>
          )}
          {/* Edit all project fields — supervisor/admin, any status. People &
              contacts have their own editor (in the People card). */}
          {canManage && (
            <Button variant="outline" size="sm" onClick={() => setEditProject(true)}>
              <Pencil className="w-3.5 h-3.5" />
              Edit project
            </Button>
          )}
          {/* Approve project — supervisor/admin only, when status is requested.
              Hidden while a budget approval is pending: approving here would
              bypass it — use the Budget approval card (which schedules) instead. */}
          {canManage && isRequested && !bidApprovalPending && (
            <ApproveScheduleButton
              project={{
                id: project.id,
                title: project.title,
                requested_start_date: project.requested_start_date,
                requested_end_date: project.requested_end_date,
                requested_schedule: project.requested_schedule,
                requested_schedule_flexible: project.requested_schedule_flexible,
                deadline_date: project.deadline_date,
                bid_total_cents: project.bid_total_cents,
                bid_approved_at: project.bid_approved_at,
                bid_mode: project.bid_mode,
              }}
              label="Approve project"
              variant="default"
            />
          )}
          {/* Confirm a supervisor-proposed schedule — the PM (or a supervisor) locks it in. */}
          {canConfirm && (
            <ConfirmScheduleButton projectId={project.id} title={project.title} />
          )}
          {/* Closeout — supervisor/admin review the recap in-app, then close + send (F.4) */}
          {canManage && !isRequested && !isCancelled && !isDeleted && (
            <Button size="sm" asChild>
              <Link to={`/projects/${project.id}/closeout`}>
                <CheckCircle2 className="w-3.5 h-3.5" />
                {isComplete ? 'Review recap' : 'Review & close'}
              </Link>
            </Button>
          )}
          {/* Restore a soft-deleted project. */}
          {canManage && isDeleted && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                restoreProject.mutate(undefined, {
                  onSuccess: () => toast.success('Project restored'),
                  onError: (e) => toast.error(e.message),
                })
              }
              disabled={restoreProject.isPending}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Restore
            </Button>
          )}
          {/* Occasional + destructive actions live in the overflow, so the
              header stays scannable (Ben 2026-08-27: "it's sprawling"). */}
          {canManage && (
            <OverflowMenu
              items={[
                ...(hasStarted
                  ? [{ label: 'Recap PDF', icon: FileDown, onClick: downloadRecapPdf }]
                  : []),
                ...(!isComplete && !isCancelled && !isDeleted
                  ? [{ label: 'Cancel project', icon: Ban, onClick: () => setConfirmCancel(true) }]
                  : []),
                ...(!isDeleted
                  ? [
                      {
                        label: 'Delete',
                        icon: Trash2,
                        onClick: () => setConfirmDelete(true),
                        danger: true,
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </div>
      </header>

      {/* Soft-deleted banner — this project is hidden from normal views. */}
      {isDeleted && (
        <div className="rounded-md border border-status-blocked-fg/30 bg-status-cancelled text-status-cancelled-fg px-4 py-2 text-sm flex items-center gap-2">
          <Trash2 className="w-4 h-4" />
          This project is deleted{project.deleted_at ? ` (${formatDate(project.deleted_at)})` : ''}.
          It’s hidden from all normal views — use Restore to bring it back.
        </div>
      )}

      <ConfirmDialog
        open={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        busy={cancelProject.isPending}
        tone="danger"
        confirmLabel="Cancel project"
        title={`Cancel "${project.title}"?`}
        body="The project is marked Cancelled and every task that isn't already complete is cancelled too, so nothing stays live on crew schedules. Completed work is kept. It stays on record (not deleted) and can't be un-cancelled from here."
        onConfirm={() =>
          cancelProject.mutate(undefined, {
            onSuccess: () => {
              toast.success('Project cancelled');
              setConfirmCancel(false);
            },
            onError: (e) => toast.error(e.message),
          })
        }
      />
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        busy={deleteProject.isPending}
        tone="danger"
        confirmLabel="Delete"
        title={`Delete "${project.title}"?`}
        body="The project is hidden from all views. You can restore it from the Deleted projects area on the Projects page."
        onConfirm={() =>
          deleteProject.mutate(undefined, {
            onSuccess: () => {
              toast.success('Project deleted');
              setConfirmDelete(false);
              navigate('/projects');
            },
            onError: (e) => toast.error(e.message),
          })
        }
      />

      {/* Key facts — the at-a-glance window, deadline, committed effort, and
          quote. Each tile renders only when its datum exists. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {(project.requested_start_date || project.requested_end_date) && (
          <div className="bg-fill-soft rounded-lg p-3">
            <div className="flex items-center gap-1 text-[11px] text-ink-faint">
              <Calendar className="w-3 h-3" /> Date range
            </div>
            <div className="font-medium text-sm mt-0.5">
              {formatDateOnly(project.requested_start_date)}
              {project.requested_end_date && ` → ${formatDateOnly(project.requested_end_date)}`}
            </div>
          </div>
        )}
        {project.deadline_date && (
          <div className="bg-fill-soft rounded-lg p-3">
            <div className="flex items-center gap-1 text-[11px] text-ink-faint">
              <Flag className="w-3 h-3" /> Hard deadline
            </div>
            <div className="font-medium text-sm mt-0.5">
              {formatDateOnly(project.deadline_date)}
              {relativeDeadline(project.deadline_date) && (
                <span className="text-ink-faint font-normal">
                  {' '}
                  · {relativeDeadline(project.deadline_date)}
                </span>
              )}
            </div>
          </div>
        )}
        {committedSchedule.length > 0 && (
          <div className="bg-fill-soft rounded-lg p-3">
            <div className="flex items-center gap-1 text-[11px] text-ink-faint">
              <Clock className="w-3 h-3" /> Committed
            </div>
            <div className="font-medium text-sm mt-0.5">
              {committedSchedule.length} day{committedSchedule.length === 1 ? '' : 's'}
              {committedHours && (
                <span className="text-ink-faint font-normal"> · {committedHours}</span>
              )}
            </div>
          </div>
        )}
        {project.bid_total_cents != null && (
          <div className="bg-fill-soft rounded-lg p-3">
            <div className="flex items-center gap-1 text-[11px] text-ink-faint">
              <DollarSign className="w-3 h-3" /> Quoted
            </div>
            <div className="font-medium text-sm mt-0.5 font-mono">
              {formatMoneyCents(project.bid_total_cents)}
            </div>
          </div>
        )}
      </div>

      {isComplete && project.completed_at && (
        <div className="rounded-md border border-status-complete-fg/30 bg-status-complete text-status-complete-fg px-4 py-2 text-sm">
          ✓ Closed {formatDate(project.completed_at)}. Recap is available to anyone with the
          public status link.
        </div>
      )}

      {showNeedsScheduling && (
        <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-4 py-2.5 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
          <span>
            <span className="font-semibold">
              {unscheduledTaskCount} task{unscheduledTaskCount > 1 ? 's' : ''} not scheduled.
            </span>{' '}
            This project is committed, but{' '}
            {unscheduledTaskCount > 1 ? 'these tasks will not' : 'this task will not'} appear on
            the dispatch board until scheduled — use{' '}
            <span className="font-medium">Schedule</span> on each task below.
          </span>
        </div>
      )}

      {bidApprovalPending && (isRequestor || canManage) && (
        <BudgetApprovalCard project={project} onBehalf={canManage && !isRequestor} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* LEFT — Schedule + Site */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-ink-faint" /> Schedule
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-[140px_1fr] gap-y-2 text-sm">
                <dt className="text-ink-faint">Requested start</dt>
                <dd>{formatDateOnly(project.requested_start_date)}</dd>
                <dt className="text-ink-faint">Requested end</dt>
                <dd>{formatDateOnly(project.requested_end_date)}</dd>
                <dt className="text-ink-faint">Hard deadline</dt>
                <dd>{formatDateOnly(project.deadline_date)}</dd>
                {(
                  project.approved_schedule ??
                  project.proposed_schedule ??
                  project.requested_schedule
                )?.length ? (
                  <>
                    <dt className="text-ink-faint">
                      {project.approved_schedule?.length
                        ? 'Committed hours'
                        : project.proposed_schedule?.length
                          ? 'Proposed hours'
                          : 'Requested hours'}
                    </dt>
                    <dd className="space-y-0.5">
                      {(
                        project.approved_schedule ??
                        project.proposed_schedule ??
                        project.requested_schedule
                      )!.map((s) => (
                        <div key={s.date}>
                          {formatDateOnly(s.date)} · {hm(s.start_time)}–{hm(s.end_time)}
                        </div>
                      ))}
                      {project.requested_schedule_flexible &&
                        !project.approved_schedule &&
                        !project.proposed_schedule && (
                          <div className="text-[11px] font-medium text-status-requested-fg">
                            Flexible — crew coordinates exact hours on site
                          </div>
                        )}
                      {!project.approved_schedule && project.proposed_schedule?.length ? (
                        <div className="text-[11px] font-medium text-status-requested-fg">
                          Awaiting the requestor&apos;s confirmation
                        </div>
                      ) : !project.approved_schedule ? (
                        <div className="text-[11px] text-ink-faint">Not yet committed</div>
                      ) : null}
                    </dd>
                  </>
                ) : null}
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-ink-faint" /> Site
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-[140px_1fr] gap-y-2 text-sm">
                <dt className="text-ink-faint">Address</dt>
                <dd>
                  {project.site_address ? (
                    <div className="flex items-start justify-between gap-2">
                      {/* Tap the address for one-tap directions — same as the crew
                          task screen. */}
                      <a
                        href={destinationUrl(project.site_address)}
                        target="_blank"
                        rel="noreferrer"
                        className="group inline-flex items-start gap-1.5 hover:text-accent"
                      >
                        <Navigation className="w-3.5 h-3.5 mt-0.5 text-ink-faint shrink-0 group-hover:text-accent" />
                        <span>
                          {project.site_address.line1}
                          {project.site_address.line2 && (
                            <>
                              <br />
                              {project.site_address.line2}
                            </>
                          )}
                          <br />
                          {project.site_address.city}, {project.site_address.state}{' '}
                          {project.site_address.postal_code}
                        </span>
                      </a>
                      <CopyButton
                        value={[
                          project.site_address.line1,
                          project.site_address.line2,
                          `${project.site_address.city}, ${project.site_address.state} ${project.site_address.postal_code}`,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                        label="Copy address"
                      />
                    </div>
                  ) : (
                    <span className="text-ink-faint">Not set</span>
                  )}
                </dd>
                <dt className="text-ink-faint">Site contact</dt>
                <dd className="flex items-center gap-1.5 flex-wrap">
                  <span>{project.site_contact_name ?? '—'}</span>
                  {project.site_contact_phone && (
                    <>
                      <span className="text-ink-faint">·</span>
                      <a
                        href={
                          phoneToTelHref(project.site_contact_phone) ??
                          `tel:${project.site_contact_phone}`
                        }
                        className="inline-flex items-center gap-1 text-accent font-medium tabular-nums hover:underline"
                      >
                        <Phone className="w-3.5 h-3.5" /> {project.site_contact_phone}
                      </a>
                      <CopyButton value={project.site_contact_phone} label="Copy phone number" />
                    </>
                  )}
                </dd>
                {project.origin_warehouse && project.origin_warehouse.length > 0 && (
                  <>
                    <dt className="text-ink-faint">
                      Origin{project.origin_warehouse.length > 1 ? 's' : ''}
                    </dt>
                    <dd>{project.origin_warehouse.join(', ')}</dd>
                  </>
                )}
                {project.site_notes && (
                  <>
                    <dt className="text-ink-faint">Site notes</dt>
                    <dd className="whitespace-pre-wrap">{project.site_notes}</dd>
                  </>
                )}
                <dt className="text-ink-faint">Status page</dt>
                <dd className="flex items-center gap-1.5 flex-wrap">
                  <a
                    href={`/status/${project.status_page_slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-accent font-medium hover:underline"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Open shareable page
                  </a>
                  <CopyButton
                    value={`${window.location.origin}/status/${project.status_page_slug}`}
                    label="Copy status page link"
                  />
                </dd>
              </dl>
              {project.site_address && (
                <SiteMap
                  latitude={project.site_address.latitude}
                  longitude={project.site_address.longitude}
                  label={`${project.title} · site`}
                  className="h-56 mt-4"
                />
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT — People & contacts + Budget */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-4 h-4 text-ink-faint" /> People & contacts
              </CardTitle>
              {canManage && (
                <Button variant="outline" size="sm" onClick={() => setEditPeople(true)}>
                  <Pencil className="w-3.5 h-3.5" /> Edit people
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-[140px_1fr] gap-y-2 text-sm">
                <dt className="text-ink-faint">Requested by</dt>
                <dd>
                  {project.pm_display_name ?? (project.source === 'form' ? '—' : 'Internal')}
                  {project.pm_email ? (
                    <span className="text-ink-faint"> · {project.pm_email}</span>
                  ) : null}
                </dd>
                <dt className="text-ink-faint">Interior designer</dt>
                <dd>{project.interior_designer ?? '—'}</dd>
                <dt className="text-ink-faint">CSM</dt>
                <dd>{project.csm ?? '—'}</dd>
                <dt className="text-ink-faint">Sales executive</dt>
                <dd>{project.sales_executive ?? '—'}</dd>
                {project.status_update_recipients &&
                  project.status_update_recipients.length > 0 && (
                    <>
                      <dt className="text-ink-faint">Other contacts</dt>
                      <dd className="space-y-0.5">
                        {project.status_update_recipients.map((r, i) => (
                          <div key={`${r.email}-${i}`}>
                            {r.name}
                            {r.email !== r.name && (
                              <span className="text-ink-faint"> · {r.email}</span>
                            )}
                          </div>
                        ))}
                      </dd>
                    </>
                  )}
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-ink-faint" /> Budget
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="text-sm space-y-2">
                <div className="flex justify-between">
                  <dt className="text-ink-faint">Proposed budget</dt>
                  <dd className="font-mono">
                    {formatMoneyCents(project.delivery_install_budget_cents)}
                  </dd>
                </div>
                {/* Quoted bid total — kept visible at every stage (the editable Bid
                    card only shows pre-schedule) so the approved figure stays on
                    screen and pairs with the actual. */}
                {project.bid_total_cents != null && (
                  <div className="flex justify-between">
                    <dt className="text-ink-faint">
                      Quoted bid
                      {project.bid_approved_at && (
                        <span className="block text-[11px] text-status-complete-fg">approved</span>
                      )}
                    </dt>
                    <dd className="font-mono">{formatMoneyCents(project.bid_total_cents)}</dd>
                  </div>
                )}
                {/* Supervisor-only decision signal (VCS-601) — PMs see their
                    allocated budget, never the system's labor estimate. */}
                {canManage && laborEstimate && (
                  <div className="flex justify-between">
                    <dt className="text-ink-faint">
                      Est. labor
                      <span className="block text-[11px] text-ink-faint/80">
                        planning estimate
                      </span>
                    </dt>
                    <dd
                      className={cn(
                        'font-mono',
                        laborEstimate.over_budget && 'text-status-blocked-fg',
                      )}
                    >
                      {laborEstimate.total_person_hours}h ·{' '}
                      {formatMoneyCents(laborEstimate.estimated_labor_cents)}
                      {laborEstimate.over_budget && ' · over'}
                    </dd>
                  </div>
                )}
                {/* Actuals only accrue once work is underway — hide the always-$0
                    row at the request stage. */}
                {(hasStarted || (project.actual_cost_cents ?? 0) > 0) && (
                  <div className="flex justify-between">
                    <dt className="text-ink-faint">Actual cost</dt>
                    <dd className="font-mono">{formatMoneyCents(project.actual_cost_cents)}</dd>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>
        </div>
      </div>

      {canManage && (
        <EditPeopleDialog
          project={project}
          open={editPeople}
          onClose={() => setEditPeople(false)}
        />
      )}

      {canManage && (
        <EditProjectDialog
          project={project}
          open={editProject}
          onClose={() => setEditProject(false)}
        />
      )}

      {/* Crew are assigned to the PROJECT directly (FMS feedback 2026-08-11,
          D-3) — this is the primary assignment; tasks below are the optional
          checklist. Hidden for a request that isn't approved yet? No: crew can
          be lined up early and get their days once the schedule is committed. */}
      <ProjectCrewCard
        project={project}
        canManage={canManage}
        readOnlyReason={isComplete ? 'complete' : isCancelled ? 'cancelled' : null}
      />

      {/* Project chat (D-9/D1) — the crew-only thread moved from the task up to
          the project. Crew + supervisors/admins only; the PM/requester is never
          a participant (that's the Updates feed). */}
      {(canManage || me?.role === 'crew') && (
        <Card>
          <CardHeader>
            <CardTitle>Project chat</CardTitle>
          </CardHeader>
          <CardContent>
            <TaskChatPanel scope={{ projectId: project.id }} />
          </CardContent>
        </Card>
      )}

      {/* Project details (D2) — Leo's one-way directives to the crew (how to run
          the job), distinct from the requester's intake notes and from chat. It
          leads the Tasks checklist below: details first, then the steps. */}
      {canManage && <ProjectDetailsCard project={project} />}

      {/* The optional checklist (B, recap #1) — the task list is part of the
          project's details (it sits right under the directives card), NOT the
          gating object: the project is scheduled and worked directly (D-3)
          whether or not it has any. Framed as "Checklist", optional, for every
          role (the checklist stays visible to PM/crew, unlike the supervisor-only
          directives card above). */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle>Checklist</CardTitle>
            <CardDescription>
              Optional steps for the crew — part of this project&rsquo;s details. The project is
              scheduled and worked without any.
            </CardDescription>
          </div>
          {/* Supervisors add committed steps; a PM can request one on an active
              project (same as the calendar) — the server marks it pending. */}
          {(canManage || (me?.role === 'pm' && !isComplete)) && (
            <AddTaskButton projectId={project.id} />
          )}
        </CardHeader>
        <CardContent>
          {canManage && !!visibleTasks.length && (
            <p className="text-[11px] text-ink-faint mb-3">
              {hasStarted
                ? 'Each step can be scheduled and assigned on its own, and shows on the dispatch board once it has a time — but the project runs whether or not the steps are done.'
                : 'Optional scope — scheduling and crew assignment open up once the project is approved. The project doesn’t need any of these to go ahead.'}
            </p>
          )}
          {!visibleTasks.length && (
            <div className="text-sm text-ink-dim">
              No checklist — this project doesn’t need one.
              {canManage && ' Add steps if it helps break the job into pieces.'}
            </div>
          )}
          {visibleTasks.map((t) => {
            // Punch tasks link through to the punch list management screen.
            const punchSummary =
              t.type === 'punch'
                ? `${t.punch_items_total - t.punch_items_open} / ${t.punch_items_total} closed`
                : null;
            const body = (
              <>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate flex items-center gap-2">
                    <span className="truncate">{t.title}</span>
                    {t.pending_confirmation && (
                      <span className="shrink-0 inline-flex items-center rounded border border-amber-300 bg-amber-50 text-amber-800 px-1.5 py-0.5 text-[10px] font-medium">
                        Pending confirmation
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ink-faint truncate">
                    {t.type}
                    {t.type === 'pickup' && ` · ${t.subtype}`}
                    {t.type === 'install' && ` · ${t.subtype}`}
                    {t.scheduled_start && ` · ${formatDateTime(t.scheduled_start)}`}
                    {punchSummary && ` · ${punchSummary}`}
                  </div>
                  {t.crew.length > 0 && (
                    <div
                      className="flex items-center mt-1.5"
                      title={t.crew.map((c) => c.display_name).join(', ')}
                    >
                      <div className="flex -space-x-1.5">
                        {t.crew.slice(0, 4).map((c, i) => (
                          <Avatar
                            key={i}
                            name={c.display_name}
                            src={c.avatar_url}
                            className="w-5 h-5 text-[9px] ring-2 ring-bg"
                          />
                        ))}
                      </div>
                      {t.crew.length > 4 && (
                        <span className="ml-1 text-[10px] text-ink-faint">
                          +{t.crew.length - 4}
                        </span>
                      )}
                    </div>
                  )}
                  {t.pending_confirmation && t.request_note && (
                    <div className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
                      <span className="font-medium">Requester’s note:</span>{' '}
                      <span className="whitespace-pre-wrap">{t.request_note}</span>
                    </div>
                  )}
                </div>
                {/* A pending task keeps lifecycle status 'scheduled' under the
                    hood — hide the whole status + chevron group, or it shows a
                    contradictory "Scheduled" (and an orphaned chevron) next to
                    the Pending badge. The amber badge is the single signal.
                    Supervisors/admins get an interactive status control in the
                    actions column instead — this static label is for everyone
                    else (and stays inside the row link). */}
                {!t.pending_confirmation && !canManage && (
                  <div
                    className={cn(
                      'text-xs flex items-center gap-1 shrink-0 whitespace-nowrap',
                      t.status === 'scheduled' && !t.scheduled_start
                        ? 'text-status-requested-fg font-medium'
                        : 'text-ink-faint',
                    )}
                  >
                    {taskStateLabel(t.status, t.scheduled_start)}
                    <ChevronRight className="w-3.5 h-3.5 text-ink-faint" />
                  </div>
                )}
              </>
            );
            const dest = t.type === 'punch' ? `/tasks/${t.id}/punch` : `/tasks/${t.id}`;
            const timelineOpen = openTimelineTaskId === t.id;
            return (
              <div key={t.id} className="border-b border-line last:border-0 -mx-5 px-5">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                <Link
                  to={dest}
                  className="flex-1 flex items-center justify-between gap-4 min-w-0 py-3 hover:text-accent transition-colors"
                >
                  {body}
                </Link>
                {canManage && (
                  <div className="flex flex-wrap items-center justify-end gap-2 sm:flex-nowrap sm:gap-3 shrink-0 pb-3 sm:pb-0">
                    {/* Chat moved to the project (D1) — the "Project chat" card
                        above is the crew-only thread; no per-task chat here. */}
                    {/* Change status in place — covers both a forward change and
                        reverting an accidental tap. Hidden on a pending task,
                        whose only meaningful action is confirm (the amber badge
                        is its single status signal). */}
                    {!t.pending_confirmation && (
                      <TaskStatusControl taskId={t.id} status={t.status} canManage />
                    )}
                    <EditTaskButton task={t} />
                    {/* Adjust the recorded execution timeline (Surface B). Available
                        for any completed task — even one closed without a running
                        timer — so a supervisor can always set the real window. */}
                    {t.status === 'complete' && (
                      <Button
                        variant={timelineOpen ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setOpenTimelineTaskId(timelineOpen ? null : t.id)}
                        aria-expanded={timelineOpen}
                      >
                        <Clock className="h-3.5 w-3.5" />
                        Adjust times
                      </Button>
                    )}
                    {t.pending_confirmation ? (
                      // A PM-requested task — confirm it (at the requested time
                      // or a different one) before it can be scheduled or crewed.
                      <ConfirmTaskButton
                        task={{
                          id: t.id,
                          title: t.title,
                          scheduled_start: t.scheduled_start,
                          scheduled_end: t.scheduled_end,
                          request_note: t.request_note,
                        }}
                      />
                    ) : (
                      <>
                        {/* Scheduling + crew assignment are premature until the
                            project is committed — withhold at the request stage. */}
                        {hasStarted && (
                          <TaskScheduleButton
                            task={t}
                            requestedDays={committedDays}
                            deadline={deadlineDay}
                            hoursLabel={committedHours}
                          />
                        )}
                        {hasStarted && <AssignCrewButton task={t} />}
                      </>
                    )}
                  </div>
                )}
                </div>
                {canManage && timelineOpen && t.status === 'complete' && (
                  <div className="pb-3">
                    <TaskTimelineEditor
                      taskId={t.id}
                      startedAt={t.started_at}
                      completedAt={t.completed_at}
                      scheduledStart={t.scheduled_start}
                      scheduledEnd={t.scheduled_end}
                      onClose={() => setOpenTimelineTaskId(null)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {project.intake_notes && (
        <Card>
          <CardHeader>
            <CardTitle>Intake notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{project.intake_notes}</p>
          </CardContent>
        </Card>
      )}

      <ProjectDocumentsCard
        projectId={project.id}
        canAdd={canManage || me?.role === 'pm'}
      />

      <ProjectManifestCard projectId={project.id} />

      <ProjectReadinessCard projectId={project.id} />

      {/* Bid editor — only while 'requested' (the quoting/approval stage). Placed
          below Tasks so the scope is set first; once scheduled, the approved total
          lives in the Budget card above. */}
      {canManage && isRequested && (
        <BidCard
          project={project}
          laborSeed={
            laborEstimate
              ? deriveLaborSeed(laborEstimate.total_task_minutes, laborEstimate.total_person_hours)
              : null
          }
          rates={rates ?? null}
        />
      )}

      {/* Field activity (crew notes / photos / punch) only happens once work is
          underway — hide the empty card + composer at the request stage. */}
      {hasStarted && <ProjectActivityCard projectId={project.id} />}
    </div>
  );
}

/**
 * Integrated bid approval (§A6) — the requestor approves (or requests a change
 * to) the bid right on the project screen, the authenticated counterpart to the
 * public token page (kept for external approvers). Shown only while a bid is
 * sent-but-not-approved.
 */
function BudgetApprovalCard({
  project,
  onBehalf = false,
}: {
  project: NonNullable<ReturnType<typeof useProject>['data']>;
  /** True when a supervisor/admin is approving on the requestor's behalf (override). */
  onBehalf?: boolean;
}) {
  const act = useBidApproval(project.id);
  const [mode, setMode] = useState<'view' | 'feedback'>('view');
  const [note, setNote] = useState('');

  const schedule =
    project.approved_schedule ?? project.proposed_schedule ?? project.requested_schedule;
  const dateLabel = schedule?.length
    ? schedule.length === 1
      ? formatDateOnly(schedule[0]!.date)
      : `${formatDateOnly(schedule[0]!.date)} – ${formatDateOnly(schedule[schedule.length - 1]!.date)}`
    : project.requested_start_date
      ? formatDateOnly(project.requested_start_date)
      : null;

  function approve() {
    act.mutate(
      { action: 'approve' },
      {
        onSuccess: (r) =>
          toast.success(r.status === 'scheduled' ? 'Approved — project scheduled' : 'Approved'),
        onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
      },
    );
  }
  function sendFeedback() {
    act.mutate(
      { action: 'feedback', note: note.trim() || undefined },
      {
        onSuccess: () => {
          toast.success('Change requested — the Vari team has been notified');
          setMode('view');
          setNote('');
        },
        onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
      },
    );
  }

  return (
    <Card className="border-accent/40 bg-accent/5">
      <CardHeader>
        <CardTitle>Budget approval</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-ink-dim">
          {onBehalf
            ? 'This bid is awaiting the requestor’s approval. As a supervisor/admin you can approve on their behalf (override) to schedule the project, or request a change.'
            : 'This bid is ready for your approval. Approve to schedule the project, or request a change.'}
        </p>
        <dl className="grid grid-cols-[140px_1fr] gap-y-2 text-sm">
          {project.bid_show_cost && project.bid_total_cents != null && (
            <>
              <dt className="text-ink-faint">Total cost</dt>
              <dd className="text-lg font-semibold font-mono">
                {formatMoneyCents(project.bid_total_cents)}
              </dd>
            </>
          )}
          {dateLabel && (
            <>
              <dt className="text-ink-faint">Proposed dates</dt>
              <dd>{dateLabel}</dd>
            </>
          )}
          {project.bid_labor_hours != null && (
            <>
              <dt className="text-ink-faint">Hours</dt>
              <dd>
                {project.bid_labor_hours} man-hours
                {project.bid_labor_crew != null && ` · crew of ${project.bid_labor_crew}`}
              </dd>
            </>
          )}
        </dl>

        {project.revision_requested_at && project.revision_note && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
            You requested a change: {project.revision_note}
          </div>
        )}

        {mode === 'view' ? (
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={approve} disabled={act.isPending}>
              {act.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setMode('feedback')}
              disabled={act.isPending}
            >
              Request a change
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What needs to change? (date, cost, scope…)"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setMode('view')}
                disabled={act.isPending}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={sendFeedback} disabled={act.isPending}>
                {act.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Send change
                request
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Bid editor (§A) — supervisor's quoted cost: a single combined labor figure,
 * round-trip mileage (× the configured rate), and a debris tier (¼–full). The
 * live total uses the shared computeBid; saving recomputes + freezes it
 * server-side. show_cost + recipient travel with the bid for the approval email.
 */
const BID_MODE_OPTIONS: { value: BidMode; label: string; hint: string }[] = [
  { value: 'none', label: 'Not used', hint: 'No bid — approving schedules the project directly.' },
  {
    value: 'internal',
    label: 'Internal only',
    hint: 'Captured for budget / analytics; not sent to the requestor. Approving schedules directly.',
  },
  {
    value: 'approval',
    label: 'Requires approval',
    hint: 'Sent to the requestor to approve before the project is scheduled.',
  },
];

function BidCard({
  project,
  laborSeed,
  rates,
}: {
  project: NonNullable<ReturnType<typeof useProject>['data']>;
  laborSeed: LaborSeed | null;
  rates: LaborRates | null;
}) {
  const { data: settings } = useCostSettings();
  const { data: recipients } = useRecipients();
  const update = useUpdateBid(project.id);
  const sendBid = useSendBid(project.id);
  const [mode, setMode] = useState<BidMode>(project.bid_mode ?? 'none');
  // Keep the toggle in sync when the project (re)loads — e.g. SPA navigation that
  // mounts the card with a stale cache, then revalidates. changeMode targets the
  // same value the refetch returns, so this never flickers the user's own click.
  useEffect(() => {
    setMode(project.bid_mode ?? 'none');
  }, [project.bid_mode]);

  const [bid, setBid] = useState<BidInputs>(() => ({
    labor_cents: project.bid_labor_cents ?? null,
    labor_roles: project.bid_labor_roles ?? null,
    mileage_miles: project.bid_mileage_miles ?? null,
    mileage_legs: project.bid_mileage_legs ?? null,
    debris_quarters: project.bid_debris_quarters ?? null,
  }));
  const routing: RoutingContext = {
    siteLat: project.site_address?.latitude ?? null,
    siteLng: project.site_address?.longitude ?? null,
    siteLabel: project.title || project.client_name || 'Job site',
    originWarehouses: project.origin_warehouse ?? [],
    dayCount:
      (project.approved_schedule ?? project.requested_schedule ?? project.proposed_schedule)?.length ||
      1,
    hasDebris: (bid.debris_quarters ?? 0) > 0,
  };
  const [showCost, setShowCost] = useState(project.bid_show_cost);
  // The requestor approves the bid, so default the recipient to the project's PM
  // (§A6); falls back to blank when there's no PM. A saved bid recipient wins.
  const [recipientName, setRecipientName] = useState(
    project.bid_recipient_name ?? project.pm_display_name ?? '',
  );
  const [recipientEmail, setRecipientEmail] = useState(
    project.bid_recipient_email ?? project.pm_email ?? '',
  );

  function bidPayload() {
    return {
      labor_cents: bid.labor_cents,
      labor_roles: bid.labor_roles,
      mileage_miles: bid.mileage_miles,
      mileage_legs: bid.mileage_legs,
      debris_quarters: bid.debris_quarters,
      show_cost: showCost,
      recipient_name: recipientName.trim() || null,
      recipient_email: recipientEmail.trim() || null,
      bid_mode: mode,
    };
  }
  // Persist the mode immediately so the approve-&-schedule flow keys off it even
  // before the bid is saved.
  function changeMode(m: BidMode) {
    setMode(m);
    update.mutate(
      { bid_mode: m },
      { onError: (e) => toast.error(`Failed: ${(e as Error).message}`) },
    );
  }
  function save() {
    update.mutate(bidPayload(), {
      onSuccess: () => toast.success('Bid saved'),
      onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
    });
  }
  async function sendForApproval() {
    try {
      // Save the current values, then email the recipient the approval link.
      await update.mutateAsync(bidPayload());
      const res = await sendBid.mutateAsync();
      if (res.ok) {
        toast.success(
          res.stub
            ? `Bid saved — approval email queued (stub) to ${res.sent_to}`
            : `Approval request sent to ${res.sent_to}`,
        );
      } else {
        toast.error('Could not send the approval email — check the address.');
      }
    } catch (e) {
      toast.error(`Failed: ${(e as Error).message}`);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bid</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Bid mode (demo #5): requestor-approval, internal-only, or no bid. */}
        <div className="space-y-1.5">
          <div className="inline-flex flex-wrap rounded-md border border-line bg-fill-soft p-0.5 text-sm">
            {BID_MODE_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => changeMode(o.value)}
                className={cn(
                  'px-3 py-1 rounded transition-colors',
                  mode === o.value
                    ? 'bg-bg shadow-sm font-medium text-ink'
                    : 'text-ink-dim hover:text-ink',
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-ink-faint">
            {BID_MODE_OPTIONS.find((o) => o.value === mode)?.hint}
          </p>
        </div>

        {mode === 'none' ? (
          <p className="text-sm text-ink-dim">
            No bid for this project — approving will schedule it directly.
          </p>
        ) : (
          <>
            <BidBuilder
              value={bid}
              onChange={setBid}
              settings={settings}
              rates={rates}
              laborSeed={laborSeed}
              routing={routing}
            />

            {mode === 'approval' && (
              <>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-ink-faint">Recipient</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <RecipientAutocomplete
                      value={recipientName}
                      onChange={setRecipientName}
                      onPick={(r) => {
                        setRecipientName(r.display_name);
                        setRecipientEmail(r.email);
                      }}
                      recipients={recipients?.data ?? []}
                      placeholder="Search the Vari directory, or type a name…"
                      className="h-9"
                    />
                    <Input
                      type="email"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      className="h-9"
                      placeholder="name@vari.com"
                    />
                  </div>
                  <p className="text-[11px] text-ink-faint">
                    Start typing to search staff, or enter a custom name + email (e.g. a VariSpace
                    contact). To send to someone else, change the recipient and resend.
                  </p>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={showCost}
                    onChange={(e) => setShowCost(e.target.checked)}
                  />
                  Show the cost to the recipient
                  <span className="text-[11px] text-ink-faint">
                    (turn off for no-cost installs / punch lists)
                  </span>
                </label>
                <RecipientPreview
                  bid={bid}
                  showCost={showCost}
                  rates={rates}
                  settings={settings ?? null}
                />
              </>
            )}

            {mode === 'approval' && project.bid_sent_at && (
              <div className="rounded-md border border-line bg-fill-soft px-3 py-2 text-xs text-ink-dim">
                Approval sent to{' '}
                <span className="font-medium text-ink">
                  {project.bid_recipient_name || project.bid_recipient_email}
                </span>{' '}
                on{' '}
                {new Date(project.bid_sent_at).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                })}
                .{' '}
                {project.bid_approved_at ? (
                  <span className="text-status-complete-fg font-medium">Approved.</span>
                ) : project.revision_requested_at ? (
                  <span className="text-amber-700 font-medium">Changes requested.</span>
                ) : (
                  <span>
                    Awaiting response — resend if they missed it, or change the recipient above to
                    send it to someone else.
                  </span>
                )}
              </div>
            )}

            {mode === 'approval' && !project.bid_sent_at && (
              <p className="text-[11px] text-ink-faint">
                Save the bid, then use <span className="font-medium">Approve project</span> above to
                send it for approval — the email includes the proposed schedule and warehouse pickup
                date.
              </p>
            )}
            {mode === 'internal' && (
              <p className="text-[11px] text-ink-faint">
                Captured for internal budget / analytics — not sent to the requestor. Approving
                schedules the project directly.
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={save}
                disabled={update.isPending || sendBid.isPending}
              >
                {update.isPending && !sendBid.isPending && (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                )}
                {project.bid_total_cents != null ? 'Update bid' : 'Save bid'}
              </Button>
              {mode === 'approval' && project.bid_sent_at && !project.bid_approved_at && (
                <Button
                  size="sm"
                  onClick={sendForApproval}
                  disabled={!recipientEmail.trim() || update.isPending || sendBid.isPending}
                  title={!recipientEmail.trim() ? 'Add a recipient email first' : undefined}
                >
                  {sendBid.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Resend approval
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Source documents (IH.6): the sales order / packing slip / design package the
 * PM supplied at intake, plus anything inserted in chat. Surfaced so the
 * approving supervisor — and the crew — can open the original paperwork.
 *
 * Supervisors + PMs can also add documents here at any time; PDFs are run
 * through the same content classifier/extractor the intake flow uses. Hidden
 * entirely when there are no documents AND the viewer can't add any.
 */
function ProjectDocumentsCard({ projectId, canAdd }: { projectId: string; canAdd: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading, error } = useProjectDocuments(projectId);
  const docs = data?.data ?? [];
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        await uploadProjectDocument({ projectId, file, analyze: true });
      }
      await qc.invalidateQueries({ queryKey: ['projects', projectId, 'documents'] });
      toast.success(files.length === 1 ? 'Document added' : `${files.length} documents added`);
    } catch (err) {
      toast.error(`Upload failed: ${(err as Error).message}`);
    } finally {
      setUploading(false);
    }
  }

  // Hidden when there are genuinely no documents and the viewer can't add any —
  // but an errored fetch must not masquerade as "no paperwork" on the screen
  // supervisors approve from.
  if (!isLoading && !error && docs.length === 0 && !canAdd) return null;

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle>Documents</CardTitle>
        {canAdd && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,image/*"
              multiple
              className="hidden"
              onChange={onPick}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyzing…
                </>
              ) : (
                <>
                  <Paperclip className="w-3.5 h-3.5" /> Add documents
                </>
              )}
            </Button>
          </>
        )}
      </CardHeader>
      <CardContent>
        {docs.length === 0 && !isLoading && !error && (
          <p className="text-sm text-ink-faint">
            No documents yet. Add the sales order, packing slip, or design package — PDFs are
            auto-classified.
          </p>
        )}
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-ink-dim">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : error ? (
          <div className="text-sm text-status-blocked-fg" role="alert">
            Couldn&apos;t load documents ({error.message}) — refresh to retry.
          </div>
        ) : (
          <ul className="divide-y divide-line -my-1">
            {docs.map((d) => (
              <li key={d.id}>
                <a
                  href={attachmentFileUrl(d)}
                  download={d.original_filename}
                  className="flex items-center gap-3 py-2.5 -mx-5 px-5 hover:bg-fill-soft transition-colors"
                >
                  {d.mime_type.startsWith('image/') ? (
                    <ImageIcon className="w-4 h-4 text-ink-faint shrink-0" />
                  ) : (
                    <FileText className="w-4 h-4 text-ink-faint shrink-0" />
                  )}
                  <span className="flex-1 min-w-0 truncate text-sm">
                    {d.original_filename}
                  </span>
                  <span
                    className={cn(
                      'text-[10px] px-1.5 py-0.5 rounded border shrink-0',
                      docKindTint(d),
                    )}
                  >
                    {docKindLabel(d)}
                  </span>
                  <span className="text-[11px] text-ink-faint shrink-0 tabular-nums">
                    {formatBytes(d.byte_size)}
                  </span>
                  <FileDown className="w-3.5 h-3.5 text-ink-faint shrink-0" />
                </a>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Site readiness (EX.5) — square footage, headcount, and the power / electrical
 * / ceiling caveats extracted from the design package's general notes. Read
 * from the design-package document's extracted_metadata; the supervisor reviews
 * these before scheduling and the crew sees them on-site. Hidden when absent.
 */
function ProjectReadinessCard({ projectId }: { projectId: string }) {
  const { data } = useProjectDocuments(projectId);
  const design = (data?.data ?? []).find((d) => d.kind === 'design_package');
  const meta = (design?.extracted_metadata ?? {}) as {
    square_footage?: unknown;
    headcount?: unknown;
    readiness_notes?: unknown;
  };
  const sqft = typeof meta.square_footage === 'number' ? meta.square_footage : null;
  const headcount = typeof meta.headcount === 'number' ? meta.headcount : null;
  const notes = Array.isArray(meta.readiness_notes)
    ? (meta.readiness_notes.filter((n) => typeof n === 'string') as string[])
    : [];
  if (sqft == null && headcount == null && notes.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Site readiness</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {(sqft != null || headcount != null) && (
          <div className="flex gap-8 text-sm">
            {sqft != null && (
              <div>
                <div className="text-xs text-ink-faint">Square footage</div>
                <div className="font-medium tabular-nums">
                  {sqft.toLocaleString()} sq ft
                </div>
              </div>
            )}
            {headcount != null && (
              <div>
                <div className="text-xs text-ink-faint">Headcount</div>
                <div className="font-medium tabular-nums">{headcount}</div>
              </div>
            )}
          </div>
        )}
        {notes.length > 0 && (
          <div>
            <div className="text-xs font-medium text-ink-faint mb-1">
              Confirm before scheduling
            </div>
            <ul className="space-y-1">
              {notes.map((n, i) => (
                <li key={i} className="flex gap-2 text-sm">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Install manifest (EX.4) — the product list extracted from intake docs:
 * packing-slip components are the crew's receiving checklist (with received /
 * installed check-off and back-order flagging), and sales-order items are the
 * commercial summary. Hidden when a project has no manifest.
 */
function ProjectManifestCard({ projectId }: { projectId: string }) {
  const { data, isLoading, error } = useProjectLineItems(projectId);
  const toggle = useToggleLineItem(projectId);
  const items = data?.data ?? [];
  if (!isLoading && !error && items.length === 0) return null;

  const packing = items.filter((i) => i.source === 'packing_slip');
  const order = items.filter((i) => i.source === 'sales_order');
  const backordered = items.filter((i) => i.is_backordered).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Manifest
          {backordered > 0 && (
            <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-status-blocked text-status-blocked-fg text-xs font-semibold px-2 py-0.5">
              <AlertTriangle className="w-3 h-3" /> {backordered} back-ordered
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-ink-dim">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : error ? (
          <div className="text-sm text-status-blocked-fg" role="alert">
            Couldn&apos;t load the manifest ({error.message}) — refresh to retry.
          </div>
        ) : (
          <>
            {packing.length > 0 && (
              <div>
                <div className="text-xs font-medium text-ink-faint mb-1">
                  Receiving · packing slip ({packing.length})
                </div>
                <ul className="divide-y divide-line">
                  {packing.map((it) => (
                    <li key={it.id} className="flex items-center gap-2 py-2">
                      <label className="flex items-center gap-1 text-[11px] text-ink-faint shrink-0" title="Received">
                        <input
                          type="checkbox"
                          checked={it.received}
                          onChange={(e) => toggle.mutate({ id: it.id, received: e.target.checked })}
                        />
                        recv
                      </label>
                      <label className="flex items-center gap-1 text-[11px] text-ink-faint shrink-0" title="Installed">
                        <input
                          type="checkbox"
                          checked={it.installed}
                          onChange={(e) => toggle.mutate({ id: it.id, installed: e.target.checked })}
                        />
                        inst
                      </label>
                      <span
                        className={cn(
                          'flex-1 min-w-0 truncate text-sm',
                          it.installed && 'line-through text-ink-faint',
                        )}
                      >
                        {it.product_code && (
                          <span className="font-mono text-[11px] text-ink-faint mr-1.5">
                            {it.product_code}
                          </span>
                        )}
                        {it.description}
                      </span>
                      {it.is_backordered && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-status-blocked text-status-blocked-fg font-semibold shrink-0 whitespace-nowrap">
                          back-ordered
                          {it.quantity != null && it.shipped != null
                            ? ` ${it.shipped}/${it.quantity}`
                            : ''}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(
              [
                { key: 'design_new', label: 'New · install' },
                { key: 'design_reused', label: 'Reused · reconfigure' },
                { key: 'design_existing', label: 'Existing · on site' },
              ] as const
            ).map(({ key, label }) => {
              const rows = items.filter((i) => i.source === key);
              if (rows.length === 0) return null;
              return (
                <div key={key}>
                  <div className="text-xs font-medium text-ink-faint mb-1">
                    {label} ({rows.length})
                  </div>
                  <ul className="divide-y divide-line">
                    {rows.map((it) => (
                      <li key={it.id} className="flex items-center gap-2 py-1.5 text-sm">
                        {it.product_code && (
                          <span className="font-mono text-[11px] text-ink-faint shrink-0">
                            {it.product_code}
                          </span>
                        )}
                        <span className="flex-1 min-w-0 truncate">{it.description}</span>
                        {it.quantity != null && (
                          <span className="text-[11px] text-ink-faint shrink-0">
                            ×{it.quantity}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
            {order.length > 0 && (
              <div>
                <div className="text-xs font-medium text-ink-faint mb-1">
                  Order · sales order ({order.length})
                </div>
                <ul className="divide-y divide-line">
                  {order.map((it) => (
                    <li key={it.id} className="flex items-center gap-2 py-1.5 text-sm">
                      {it.product_code && (
                        <span className="font-mono text-[11px] text-ink-faint shrink-0">
                          {it.product_code}
                        </span>
                      )}
                      <span className="flex-1 min-w-0 truncate">{it.description}</span>
                      {it.quantity != null && (
                        <span className="text-[11px] text-ink-faint shrink-0">×{it.quantity}</span>
                      )}
                      {it.total_cents != null && (
                        <span className="text-[11px] text-ink-faint shrink-0 tabular-nums">
                          {formatMoneyCents(it.total_cents)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Cross-task field activity (Workspace.3): notes, photos/videos, and punch
 * items the crew captured — so PM / Sales / Supervisor see it on the project.
 */
function ProjectActivityCard({ projectId }: { projectId: string }) {
  const { data, isLoading, error } = useProjectActivity(projectId);
  const items = data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity</CardTitle>
      </CardHeader>
      <CardContent>
        <ProjectUpdateComposer projectId={projectId} />
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-ink-dim">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : error ? (
          <div className="text-sm text-status-blocked-fg" role="alert">
            Couldn&apos;t load activity ({error.message}) — refresh to retry.
          </div>
        ) : items.length === 0 ? (
          <div className="text-sm text-ink-dim">
            No field activity yet — notes, photos, videos, and punch items from the crew show up here.
          </div>
        ) : (
          <ul className="space-y-4">
            {items.map((it) => (
              <li key={`${it.kind}-${it.id}`} className="flex gap-3">
                <div className="mt-0.5 flex-shrink-0">
                  {it.kind === 'note' && <MessageSquare className="w-4 h-4 text-ink-faint" />}
                  {it.kind === 'punch' && (
                    <AlertTriangle
                      className={cn(
                        'w-4 h-4',
                        it.punch_status === 'closed' ? 'text-ink-faint' : 'text-status-blocked-fg',
                      )}
                    />
                  )}
                  {it.kind === 'media' && <ImageIcon className="w-4 h-4 text-ink-faint" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-ink-faint">
                    {formatDateTime(it.at)}
                    {it.author_name && ` · ${it.author_name}`}
                    {it.task_title && ` · ${it.task_title}`}
                  </div>
                  {it.kind === 'punch' && it.title && (
                    <div
                      className={cn(
                        'text-sm font-medium',
                        it.punch_status === 'closed' && 'line-through text-ink-faint',
                      )}
                    >
                      {it.title}
                      {it.blocks_recap && it.punch_status !== 'closed' && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-status-blocked-fg font-semibold">
                          Blocks closeout
                        </span>
                      )}
                    </div>
                  )}
                  {it.body && <div className="text-sm whitespace-pre-wrap">{it.body}</div>}
                  <MediaGallery media={it.media} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Project-level "Add update" composer (Workspace.3b) — office (PM / Sales /
 * Supervisor) posts a note + photos/videos directly on the project, not tied to
 * a task. Mirrors the crew task composer, minus the punch flag.
 */
function ProjectUpdateComposer({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [usedVoice, setUsedVoice] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const addNote = useAddProjectNote(projectId);
  const { supported, listening, toggle } = useDictation((t) => {
    setText((p) => (p ? `${p} ${t}` : t));
    setUsedVoice(true);
  });

  const previews = useMemo(
    () => files.map((f) => ({ url: URL.createObjectURL(f), isVideo: f.type.startsWith('video/') })),
    [files],
  );
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

  const body = text.trim();
  const canSave = (body.length > 0 || files.length > 0) && !saving;

  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      let noteId: string | null = null;
      if (body) {
        const note = await addNote.mutateAsync({ body, source: usedVoice ? 'voice' : 'text' });
        noteId = note.id;
      }
      for (const f of files) await uploadProjectMedia(projectId, f, noteId);
      qc.invalidateQueries({ queryKey: ['projects', projectId, 'activity'] });
      setText('');
      setFiles([]);
      setUsedVoice(false);
      toast.success('Update added');
    } catch (err) {
      toast.error(`Failed: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mb-4 space-y-2 border-b border-line pb-4">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Add a note, photo, or video to this project…"
        rows={2}
        className="w-full rounded-lg border border-line bg-fill-soft p-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-accent"
      />
      {previews.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {previews.map((p, i) => (
            <div
              key={i}
              className="relative w-16 h-16 rounded-lg overflow-hidden border border-line bg-fill-soft"
            >
              {p.isVideo ? (
                <video src={p.url} className="w-full h-full object-cover" muted />
              ) : (
                <img src={p.url} alt="" className="w-full h-full object-cover" />
              )}
              <button
                type="button"
                onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                className="absolute top-0.5 right-0.5 bg-ink/70 text-bg rounded-full p-0.5"
                aria-label="Remove"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={(e) => {
          setFiles((prev) => [...prev, ...Array.from(e.target.files ?? [])]);
          e.target.value = '';
        }}
      />
      <div className="flex gap-2">
        <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()}>
          <Paperclip className="w-3.5 h-3.5" /> Photo / video
        </Button>
        {supported && (
          <Button type="button" size="sm" variant={listening ? 'default' : 'outline'} onClick={toggle}>
            <Mic className="w-3.5 h-3.5" /> {listening ? 'Listening…' : 'Dictate'}
          </Button>
        )}
        <Button size="sm" className="flex-1" onClick={save} disabled={!canSave}>
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save update
        </Button>
      </div>
    </div>
  );
}

/**
 * Header overflow ("⋯") for occasional + destructive actions, so the header
 * row stays scannable (Ben 2026-08-27). Same popup idiom as the status
 * controls: outside-click + Escape close, right-aligned.
 */
function OverflowMenu({
  items,
}: {
  items: Array<{
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    onClick: () => void;
    danger?: boolean;
  }>;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <div ref={wrapRef} className="relative inline-block">
      <Button
        variant="outline"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
        onClick={() => setOpen((o) => !o)}
      >
        <MoreHorizontal className="w-4 h-4" />
      </Button>
      {open && (
        <div
          role="menu"
          aria-label="More actions"
          className="absolute right-0 z-50 mt-1 min-w-44 overflow-hidden rounded-md border border-line bg-bg py-1 shadow-lg"
        >
          {items.map(({ label, icon: Icon, onClick, danger }) => (
            <button
              key={label}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onClick();
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-fill-soft',
                danger ? 'text-status-blocked-fg' : 'text-ink',
              )}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
